import React, { useMemo, useRef } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { H2, Line, Button, Flex } from '../../lib/components'
import { FileUploadIcon, FileUploadedIcon, DeleteIcon } from '../../lib/components/Icon'
import { BlogFormData } from '../../services/blog-service'
import './blogs-form.scss'
import { BLOG_CATEGORIES, BLOG_STATUSES } from './constants'
import { CircularProgress } from '@mui/material'
import ReactQuill from 'react-quill'

type BlogsFormPresenterProps = {
  form: UseFormReturn<BlogFormData>
  isEditMode: boolean
  isSubmitting: boolean
  isLoading: boolean
  bannerPreview: string
  onSubmit: (data: BlogFormData) => Promise<void>
  onTitleBlur: () => void
  onFileUpload: (file: File) => Promise<void>
  onRemoveBanner: () => void
}

function BlogsFormPresenter({
  form,
  isEditMode,
  isSubmitting,
  isLoading,
  bannerPreview,
  onSubmit,
  onTitleBlur,
  onFileUpload,
  onRemoveBanner,
}: BlogsFormPresenterProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
  } = form

  const bannerImage = watch('banner_image')
  const content = watch('content')

  const quillRef = useRef<ReactQuill>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) onFileUpload(file)
    e.target.value = ''
  }

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ font: [] }],
          [{ size: ['small', false, 'large', 'huge'] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          ['blockquote', 'code-block'],
          ['link', 'image'],
          ['clean'],
        ],
      },
      clipboard: { matchVisual: false },
    }),
    [],
  )

  const quillFormats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'align',
    'list',
    'bullet',
    'indent',
    'blockquote',
    'code-block',
    'link',
    'image',
  ]

  return (
    <>
      {isLoading && (
        <div className='loading-overlay'>
          <CircularProgress size={40} color='primary' className='loading-overlay__spinner' />
        </div>
      )}

      <Flex variant='column-center'>
        <H2 classes={['mb-24']} text={isEditMode ? 'Edit Blog' : 'Add New Blog'} />

        <form className='blog-form' onSubmit={handleSubmit(onSubmit)} noValidate>
          <Line />

          {/* Title */}
          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='title'>
              Title <span className='blog-form__required'>*</span>
            </label>
            <input
              id='title'
              className={`blog-form__input ${errors.title ? 'blog-form__input--error' : ''}`}
              placeholder='Enter blog title'
              maxLength={255}
              {...register('title', {
                required: 'Title is required',
                maxLength: { value: 255, message: 'Title must be at most 255 characters' },
              })}
              onBlur={(e) => {
                register('title').onBlur(e)
                onTitleBlur()
              }}
            />
            {errors.title && <span className='blog-form__error'>{errors.title.message}</span>}
          </div>

          {/* Estimated reading time */}
          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='reading_time_minutes'>
              Estimated reading time (minutes)
            </label>
            <input
              id='reading_time_minutes'
              type='number'
              min={1}
              max={999}
              className={`blog-form__input ${
                errors.reading_time_minutes ? 'blog-form__input--error' : ''
              }`}
              placeholder='e.g. 8'
              {...register('reading_time_minutes', {
                setValueAs: (value) => {
                  if (value === '' || value === null || value === undefined) {
                    return undefined
                  }
                  const n = typeof value === 'string' ? parseInt(value, 10) : Number(value)
                  return Number.isFinite(n) ? n : undefined
                },
                validate: (value) =>
                  value === undefined ||
                  value === null ||
                  (Number.isInteger(value) && value >= 1 && value <= 999) ||
                  'Enter a whole number between 1 and 999',
              })}
            />
            <span className='blog-form__hint'>
              Shown on the public blog as reading time under the title (optional).
            </span>
            {errors.reading_time_minutes && (
              <span className='blog-form__error'>{errors.reading_time_minutes.message}</span>
            )}
          </div>

          {/* Subtitle */}
          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='sub_title'>
              Subtitle <span className='blog-form__required'>*</span>
            </label>
            <textarea
              id='sub_title'
              className={`blog-form__input blog-form__textarea ${
                errors.sub_title ? 'blog-form__input--error' : ''
              }`}
              placeholder='Enter blog sub title'
              maxLength={500}
              rows={2}
              {...register('sub_title', {
                required: 'Subtitle is required',
                maxLength: { value: 500, message: 'Subtitle must be at most 500 characters' },
              })}
            />
            {errors.sub_title && (
              <span className='blog-form__error'>{errors.sub_title.message}</span>
            )}
          </div>

          {/* Slug */}
          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='slug'>
              Slug <span className='blog-form__required'>*</span>
            </label>
            <input
              id='slug'
              className={`blog-form__input ${errors.slug ? 'blog-form__input--error' : ''}`}
              placeholder='blog-post-slug'
              maxLength={500}
              {...register('slug', {
                required: 'Slug is required',
                maxLength: { value: 500, message: 'Slug must be at most 500 characters' },
                pattern: {
                  value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                  message: 'Slug must be lowercase letters, numbers, and hyphens only',
                },
              })}
            />
            <span className='blog-form__hint'>
              Unique URL-friendly identifier (auto-generated from title)
            </span>
            {errors.slug && <span className='blog-form__error'>{errors.slug.message}</span>}
          </div>

          {/* Content */}
          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='content'>
              Content <span className='blog-form__required'>*</span>
            </label>
            <div className='blog-form__editor-wrapper'>
              <ReactQuill
                ref={quillRef}
                theme='snow'
                value={content}
                onChange={(content) => {
                  setValue('content', content, { shouldDirty: true, shouldValidate: true })
                  clearErrors('content')
                }}
                modules={quillModules}
                formats={quillFormats}
                placeholder='Write your blog content here...'
              />
            </div>
            <span className='blog-form__hint'>
              Use the toolbar to format text, insert images, add links, and more. You can also paste
              content from other sources.
            </span>
            {errors.content && <span className='blog-form__error'>{errors.content.message}</span>}
          </div>

          {/* Banner Image */}
          <div className='blog-form__field'>
            <label className='blog-form__label'>
              Banner Image <span className='blog-form__required'>*</span>
            </label>
            <div className='blog-form__file-upload'>
              <label
                className={`blog-form__file-dropzone ${
                  bannerImage ? 'blog-form__file-dropzone--has-file' : ''
                }`}
                htmlFor='banner_image_input'
              >
                {!bannerImage ? (
                  <>
                    <FileUploadIcon />
                    <div className='blog-form__file-info'>
                      <span className='blog-form__file-info-primary'>Browse to upload</span>
                      <span className='blog-form__file-info-secondary'>
                        Supported file types: JPEG, JPG up to 10 MB
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <FileUploadedIcon />
                    <div className='blog-form__file-info'>
                      <span className='blog-form__file-info-primary'>Image uploaded</span>
                    </div>
                    <span
                      className='blog-form__file-delete'
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        onRemoveBanner()
                      }}
                    >
                      <DeleteIcon dataTestId='banner-delete-button' />
                    </span>
                  </>
                )}
                <input
                  id='banner_image_input'
                  type='file'
                  accept='image/jpg, image/jpeg'
                  className='blog-form__file-hidden'
                  onChange={handleFileChange}
                />
              </label>
            </div>
            {bannerPreview && (
              <div className='blog-form__banner-preview'>
                <img src={bannerPreview} alt='Banner preview' />
              </div>
            )}
            {errors.banner_image && (
              <span className='blog-form__error'>{errors.banner_image.message}</span>
            )}
          </div>

          {/* Status */}
          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='status'>
              Status <span className='blog-form__required'>*</span>
            </label>
            <select
              id='status'
              className='blog-form__input'
              {...register('status', { required: 'Status is required' })}
            >
              {BLOG_STATUSES.map((status) => (
                <option key={status.value} value={status.value} defaultChecked={status.default}>
                  {status.text}
                </option>
              ))}
            </select>
            {errors.status && <span className='blog-form__error'>{errors.status.message}</span>}
          </div>

          {/* Category */}
          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='category'>
              Category <span className='blog-form__required'>*</span>
            </label>
            <select
              id='category'
              className='blog-form__input'
              {...register('category', { required: 'Category is required' })}
            >
              {BLOG_CATEGORIES.map((category) => (
                <option
                  key={category.value}
                  value={category.value}
                  defaultChecked={category.default}
                >
                  {category.text}
                </option>
              ))}
            </select>
            {errors.category && <span className='blog-form__error'>{errors.category.message}</span>}
          </div>

          {/* LinkedIn URL */}
          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='linkedin_url'>
              LinkedIn URL
            </label>
            <input
              id='linkedin_url'
              className={`blog-form__input ${errors.linkedin_url ? 'blog-form__input--error' : ''}`}
              placeholder='Enter LinkedIn URL here...'
              {...register('linkedin_url', {
                pattern: {
                  value: /^https:\/\/.+$/i,
                  message:
                    'Invalid URL. Please enter a valid URL. Example: https://your-linkedin-url.com',
                },
              })}
            />
            {errors.linkedin_url && (
              <span className='blog-form__error'>{errors.linkedin_url.message}</span>
            )}
          </div>

          <Line />

          {/* Actions */}
          <div className='blog-form__actions'>
            <Button
              buttonType='submit'
              variant='primary-btn'
              handleClick={() => {}}
              text={isSubmitting ? 'Saving...' : isEditMode ? 'Update Blog' : 'Create Blog'}
              isDisabled={isSubmitting || isLoading}
            />
          </div>
        </form>
      </Flex>
    </>
  )
}

export { BlogsFormPresenter }
